/**
 * Metadatos del dataset (única fuente de verdad).
 *
 * Todo el sistema (tabla, menú de filtros, editor de gráficos) se genera
 * automáticamente a partir de esta definición. Para añadir una columna nueva
 * basta con declararla aquí: aparecerá en la tabla, en los filtros dinámicos
 * y como dimensión/métrica en los gráficos sin tocar ningún componente.
 *
 * type:
 *   'text'    → texto libre
 *   'enum'    → conjunto cerrado de valores (se filtra con multiselección)
 *   'number'  → numérico (puede usarse como métrica)
 *   'date'    → fecha ISO (yyyy-mm-dd)
 *   'boolean' → sí / no
 */

export const FIELDS = [
  // --- Obra / proyecto -----------------------------------------------------
  {
    key: 'codigoObra',
    label: 'Código obra',
    type: 'text',
    group: 'Obra',
    table: { width: 120, mono: true },
  },
  { key: 'obra', label: 'Obra', type: 'text', group: 'Obra', table: { width: 200 } },
  { key: 'codZona', label: 'Zona', type: 'enum', group: 'Localización', labelMap: [undefined, 'Europa', 'México', 'Países no GOA'] },
  { key: 'proyecto', label: 'Proyecto', type: 'enum', group: 'Obra' },
  { key: 'cadena', label: 'Cadena', type: 'enum', group: 'Obra' },
  { key: 'tipoObra', label: 'Tipo de obra', type: 'enum', group: 'Obra' },
  { key: 'fechaFin', label: 'Fecha fin', type: 'date', group: 'Obra' },
  { key: 'provincia', label: 'Provincia', type: 'enum', group: 'Localización' },
  { key: 'comunidad', label: 'Comunidad', type: 'enum', group: 'Localización' },
  { key: 'pais', label: 'País', type: 'enum', group: 'Localización' },
  {
    key: 'superficie',
    label: 'Superficie obra',
    type: 'number',
    group: 'Obra',
    unit: 'm²',
    decimals: 0,
  },

  // --- Residuo -------------------------------------------------------------
  {
    key: 'codigoLER',
    label: 'Código LER',
    type: 'text',
    group: 'Residuo',
    table: { width: 110, mono: true },
  },
  { key: 'residuo', label: 'Residuo', type: 'enum', group: 'Residuo', table: { width: 220 } },
  { key: 'tipoResiduo', label: 'Tipo de residuo', type: 'enum', group: 'Residuo' },
  { key: 'peligroso', label: 'Peligroso', type: 'boolean', group: 'Residuo' },
  { key: 'segrega', label: 'Segrega', type: 'boolean', group: 'Residuo' },
  { key: 'estadoFisico', label: 'Estado físico', type: 'enum', group: 'Residuo' },

  // --- Gestión -------------------------------------------------------------
  { key: 'tipoGestion', label: 'Tipo de gestión', type: 'enum', group: 'Gestión' },
  { key: 'tratamiento', label: 'Tratamiento', type: 'enum', group: 'Gestión' },
  { key: 'transportista', label: 'Transportista', type: 'enum', group: 'Gestión' },
  { key: 'gestor', label: 'Gestor final', type: 'enum', group: 'Gestión' },
  { key: 'estado', label: 'Estado', type: 'enum', group: 'Gestión' },
  {
    key: 'numDocumento',
    label: 'Nº documento',
    type: 'text',
    group: 'Gestión',
    table: { width: 130, mono: true },
  },

  // --- Fechas --------------------------------------------------------------
  { key: 'fechaTransporte', label: 'Fecha transporte', type: 'date', group: 'Fechas' },
  { key: 'fechaTratamiento', label: 'Fecha tratamiento', type: 'date', group: 'Fechas' },

  // --- Magnitudes ----------------------------------------------------------
  { key: 'peso', label: 'Peso', type: 'number', group: 'Magnitudes', unit: 'kg', decimals: 2 },
  { key: 'volumen', label: 'Volumen', type: 'number', group: 'Magnitudes', unit: 'm³', decimals: 2 },
  { key: 'km', label: 'Kilómetros', type: 'number', group: 'Magnitudes', unit: 'km', decimals: 0 },
  { key: 'viajes', label: 'Nº viajes', type: 'number', group: 'Magnitudes', decimals: 0 },
  {
    key: 'porcentajeValorizacion',
    label: '% Valorización',
    type: 'number',
    group: 'Magnitudes',
    unit: '%',
    decimals: 1,
  },

  // --- Costes --------------------------------------------------------------
  {
    key: 'costeGestion',
    label: 'Coste gestión',
    type: 'number',
    group: 'Costes',
    unit: '€',
    decimals: 2,
  },
  {
    key: 'costeTransporte',
    label: 'Coste transporte',
    type: 'number',
    group: 'Costes',
    unit: '€',
    decimals: 2,
  },
  {
    key: 'costeTotal',
    label: 'Coste total',
    type: 'number',
    group: 'Costes',
    unit: '€',
    decimals: 2,
  },
  {
    key: 'costeReal',
    label: 'Coste real',
    type: 'number',
    group: 'Costes',
    unit: '€',
    decimals: 2,
  },
  {
    key: 'costePrevisto',
    label: 'Coste previsto',
    type: 'number',
    group: 'Costes',
    unit: '€',
    decimals: 2,
  },
  {
    key: 'costeColocacion',
    label: 'Coste de colocación',
    type: 'number',
    group: 'Costes',
    unit: '€',
    decimals: 2,
  },
  {
    key: 'costeAlquiler',
    label: 'Coste de alquiler',
    type: 'number',
    group: 'Costes',
    unit: '€',
    decimals: 2,
  },
  {
    key: 'costeDocumento',
    label: 'Coste de documento',
    type: 'number',
    group: 'Costes',
    unit: '€',
    decimals: 2,
  },
  {
    key: 'abonoChatarra',
    label: 'Abono de chatarra',
    type: 'number',
    group: 'Costes',
    unit: '€',
    decimals: 2,
  },


// --- Cantidades Generadas --------------------------------------------------------------
  {
    key: 'cantidadValorizada',
    label: 'Cantidad valorizada',
    type: 'number',
    unit: 'kg',
    decimals: 2,
    group: 'residuos',
  },
  {
    key: 'cantidadEliminada',
    label: 'Cantidad eliminada',
    type: 'number',
    unit: 'kg',
    decimals: 2,
    group: 'residuos',
  },

// --- Porcentajes por cadena (window functions) --------------------------------
  {
    key: 'pctValorizacion',
    label: '% Valorización',
    type: 'number',
    unit: '%',
    decimals: 2,
    group: 'Tratamiento',
  },
  {
    key: 'pctSegregacion',
    label: '% Segregación',
    type: 'number',
    unit: '%',
    decimals: 2,
    group: 'Tratamiento',
  },
  {
    key: 'pctValorizacionTipoObra',
    label: '% Valorización (Tipo obra)',
    type: 'number',
    unit: '%',
    decimals: 2,
    group: 'Tratamiento',
  },
  {
    key: 'pctSegregacionTipoObra',
    label: '% Segregación (Tipo obra)',
    type: 'number',
    unit: '%',
    decimals: 2,
    group: 'Tratamiento',
  },
  {
    key: 'pctValorizacionPais',
    label: '% Valorización (País)',
    type: 'number',
    unit: '%',
    decimals: 2,
    group: 'Tratamiento',
  },
  {
    key: 'pctSegregacionPais',
    label: '% Segregación (País)',
    type: 'number',
    unit: '%',
    decimals: 2,
    group: 'Tratamiento',
  },

// --- Segregación Total (RNP vs Mezcla) ---------------------------------------
  {
    key: 'pctSegregacionTotal',
    label: '% Segregación Total',
    type: 'number',
    unit: '%',
    decimals: 2,
    group: 'Segregación Total',
  },
  {
    key: 'pctMezcla',
    label: '% Mezcla',
    type: 'number',
    unit: '%',
    decimals: 2,
    group: 'Segregación Total',
  },
  {
    key: 'pctSegregacionTotalTipoObra',
    label: '% Segregación Total (Tipo obra)',
    type: 'number',
    unit: '%',
    decimals: 2,
    group: 'Segregación Total',
  },
  {
    key: 'pctMezclaTipoObra',
    label: '% Mezcla (Tipo obra)',
    type: 'number',
    unit: '%',
    decimals: 2,
    group: 'Segregación Total',
  },
  {
    key: 'pctSegregacionTotalPais',
    label: '% Segregación Total (País)',
    type: 'number',
    unit: '%',
    decimals: 2,
    group: 'Segregación Total',
  },
  {
    key: 'pctMezclaPais',
    label: '% Mezcla (País)',
    type: 'number',
    unit: '%',
    decimals: 2,
    group: 'Segregación Total',
  },

// --- Huella de carbono -------------------------------------------------------
  {
    key: 'emisionesCO2',
    label: 'Emisiones CO₂',
    type: 'number',
    unit: 'kg',
    decimals: 3,
    group: 'Huella de carbono',
  },
  {
    key: 'emisionesCH4',
    label: 'Emisiones CH₄',
    type: 'number',
    unit: 'kg',
    decimals: 3,
    group: 'Huella de carbono',
  },
  {
    key: 'emisionesN2O',
    label: 'Emisiones N₂O',
    type: 'number',
    unit: 'kg',
    decimals: 3,
    group: 'Huella de carbono',
  },
  {
    key: 'emisionesTotales',
    label: 'Emisiones totales',
    type: 'number',
    unit: 'kg',
    decimals: 3,
    group: 'Huella de carbono',
  },
]

export const FIELD_MAP = FIELDS.reduce((acc, field) => {
  acc[field.key] = field
  return acc
}, {})

export function getField(key) {
  return FIELD_MAP[key]
}

/**
 * Resuelve un campo buscando primero en una lista local de campos (si se
 * indica, p. ej. los filterFields propios de una tabla) y cae al registro
 * global cuando no existe localmente.
 */
export function resolveField(key, fields) {
  if (Array.isArray(fields)) {
    const local = fields.find((field) => field.key === key)
    if (local) return local
  }
  return FIELD_MAP[key]
}

/** Campos que pueden usarse como dimensión (eje X / agrupación). */
export const DIMENSION_FIELDS = FIELDS.filter((f) =>
  ['enum', 'text', 'date', 'boolean'].includes(f.type)
)

/** Campos numéricos: candidatos a métrica de un gráfico. */
export const METRIC_FIELDS = FIELDS.filter((f) => f.type === 'number')

/** Agrupación de campos por bloque, para pintar menús ordenados. */
export function groupFields(fields) {
  const groups = []
  fields.forEach((field) => {
    const name = field.group || 'Otros'
    let group = groups.find((g) => g.name === name)
    if (!group) {
      group = { name, fields: [] }
      groups.push(group)
    }
    group.fields.push(field)
  })
  return groups
}

/**
 * Calcula los valores distintos de un campo a partir de las filas.
 * Se usa para que los filtros de tipo lista sean 100% dinámicos.
 */
export function distinctValues(rows, key, fields) {
  const map = new Map()
  rows.forEach((row) => {
    let value = row[key]
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const field = fields?.find?.((item) => item.key === key)
      if (field?.objectProp && value[field.objectProp] !== undefined) {
        value = value[field.objectProp]
      }
    }
    if (value !== null && value !== undefined && value !== '') {
      const normalizedKey = typeof value === 'number' ? String(value) : value
      if (!map.has(normalizedKey)) {
        map.set(normalizedKey, value)
      }
    }
  })
  return Array.from(map.values()).sort((a, b) =>
    typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b), 'es')
  )
}

const humanizeKey = (key) =>
  key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim()

const guessFieldType = (value) => {
  if (typeof value === 'boolean') return 'boolean'
  if (typeof value === 'number') return 'number'
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return 'date'
  return 'text'
}

/**
 * Genera los campos de filtro a partir de las props reales de un objeto.
 * Se usa solo para el selector de filtros avanzados: cada clave del item se
 * ofrece como posible campo de filtrado, de modo que no hace falta declarar
 * los campos opcionales en cada pantalla.
 *
 * - Los campos ya registrados en FIELDS conservan sus metadatos (label/type/group).
 * - Las claves nuevas se infieren según el valor (number/boolean/date/text).
 * - Los objetos/arrays anidados se descartan (no son comparables directamente).
 */
export function inferObjectFields(rows) {
  const first = Array.isArray(rows) ? rows[0] : rows
  if (!first || typeof first !== 'object') return []

  const fields = []
  Object.keys(first).forEach((key) => {
    const value = first[key]
    if (value !== null && typeof value === 'object') return
    const existing = FIELD_MAP[key]
    if (existing) {
      fields.push(existing)
      return
    }
    fields.push({
      key,
      label: humanizeKey(key),
      type: guessFieldType(value),
    })
  })
  return fields
}
